import { useState, useMemo } from "react";
// V62.5 — source from new periodicTable.js data module (replaces elements.json).
// ELEMENTS array shape is a superset of the old JSON; field-key compatibility
// is preserved (number, symbol, name, mass, category, row, col, config,
// state, density, melt, boil, electroneg, discovered). New fields
// (period, group, block, categoryLabel, electronAffinity, ionizationEnergy,
// atomicRadius, commonCharges, commonOxidationStates, discoveredBy) are
// available via the same array.
import { ELEMENTS as elements } from "../data/periodicTable.js";
import { NerdStackFooter } from "../components/NerdStackFooter.jsx";
import QuizMode from "../components/QuizMode.jsx";
import PeriodicTableMCQuiz from "./PeriodicTableMCQuiz.jsx";
import { ChemBackButton } from "./ChemBackButton.jsx";
import PeriodicCompare from "./PeriodicCompare.jsx";

// NerdChem accent — emerald per docs/family/01-nerdchem.md
const EM = "#F8B84E";       // locked NerdChem in-app accent (icon gradient start hue)
const EM_DEEP = "#7C3AED";  // family violet — makes the Ch badge gradient amber→violet
const EM_LIGHT = "#FCD9A0"; // light amber for gradient-text headings

const rgba = (hex, a) => {
  const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
};

// Category palette — distinct, readable on the dark bg, colorblind-considered.
const CAT = {
  alkali:          { c: "#F87171", label: "Alkali metal" },
  alkaline:        { c: "#FB923C", label: "Alkaline earth" },
  transition:      { c: "#FBBF24", label: "Transition metal" },
  "post-transition": { c: "#34D399", label: "Post-transition" },
  metalloid:       { c: "#22D3EE", label: "Metalloid" },
  nonmetal:        { c: "#60A5FA", label: "Nonmetal" },
  halogen:         { c: "#A78BFA", label: "Halogen" },
  noble:           { c: "#F472B6", label: "Noble gas" },
  lanthanide:      { c: "#2DD4BF", label: "Lanthanide" },
  actinide:        { c: "#C084FC", label: "Actinide" },
  unknown:         { c: "#6B7280", label: "Unknown" },
};

function ElementCell({ el, onClick, dimmed, glowing, searchOutlined, pinned }) {
  const cat = CAT[el.category] || CAT.unknown;
  return (
    <button
      // V42.1 fix — `onClick={dimmed ? undefined : ...}` ALONE did not actually
      // block clicks: native <button> with no React handler still registers as
      // a pressable button to the browser, and any code that finds the cell
      // via DOM still treats it as clickable. `disabled={dimmed}` is the real
      // block — native HTML inert state, no click event fires, no focus, no
      // hover response. Combined with cursor:default + suppressed hover-scale,
      // this finally reads as "intentionally off" instead of "glitched".
      disabled={dimmed}
      onClick={() => onClick(el)}
      title={el.name + (pinned ? " (pinned for compare)" : "")}
      style={{
        gridRow: el.row, gridColumn: el.col,
        aspectRatio: "1 / 1", minWidth: 0,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        background: dimmed ? "rgba(255,255,255,0.02)" : rgba(cat.c, 0.13),
        // Border priority: pinned > search-outline > category glow > normal cat border > dimmed.
        // V62.5 — pinned outline (cyan #67E8F9) takes priority over search-outline
        // (violet #C4B5FD) and category glow. The cyan is intentionally distinct
        // from any category color so pinned-ness is unambiguous.
        // V42.1 — search-outline now #C4B5FD (violet-300). #DDD6FE (violet-200)
        // read as near-white in Ian's visual check 2026-05-20; midpoint between
        // that and Halogen #A78BFA brings real violet identity back while
        // still standing off Halogen and Actinide. If THIS reads muddy on
        // Halogen/Actinide filters, the next fallback is darker (e.g. #B5A3FC)
        // NOT lighter — opposite direction from the original spec fallback.
        border: pinned
          ? "2px solid #67E8F9"
          : searchOutlined
            ? "2px solid #C4B5FD"
            : glowing
              ? `2px solid ${cat.c}`
              : `1px solid ${dimmed ? "rgba(255,255,255,0.05)" : rgba(cat.c, 0.4)}`,
        boxShadow: pinned
          ? "0 0 10px rgba(103, 232, 249, 0.6)"
          : searchOutlined
            ? "0 0 8px rgba(196, 181, 253, 0.55)"
            : glowing
              ? `0 0 10px ${rgba(cat.c, 0.55)}`
              : "none",
        borderRadius: "clamp(2px, 0.6vw, 6px)",
        position: "relative",
        // Intentionally-off cursor for dimmed cells: NOT pointer, signals
        // "this is deliberately not clickable" rather than "site glitched".
        cursor: dimmed ? "default" : "pointer",
        padding: "clamp(1px, 0.3vw, 3px)",
        fontFamily: "inherit", color: "#E2E8F0", overflow: "hidden",
        opacity: dimmed ? 0.35 : 1, transition: "opacity 0.15s, transform 0.1s, box-shadow 0.15s",
        // Native <button disabled> often dims background/text via browser default;
        // we already control opacity explicitly above, so make sure browser
        // defaults don't double-dim or shift colors.
        WebkitAppearance: "none",
        appearance: "none",
      }}
      // Suppress hover scale for dimmed cells — the lift implies clickable,
      // and we need the dimmed cells to NOT imply clickability.
      onMouseEnter={dimmed ? undefined : (e) => { e.currentTarget.style.transform = "scale(1.08)"; e.currentTarget.style.zIndex = 5; }}
      onMouseLeave={dimmed ? undefined : (e) => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.zIndex = 1; }}
    >
      {/* V62.5 — pinned indicator dot (top-right of cell).
          Small cyan circle, only visible when pinned. Doesn't interfere
          with cell content because it's absolutely positioned. */}
      {pinned && (
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            top: "clamp(1px, 0.3vw, 3px)",
            right: "clamp(1px, 0.3vw, 3px)",
            width: "clamp(4px, 0.9vw, 7px)",
            height: "clamp(4px, 0.9vw, 7px)",
            borderRadius: "50%",
            background: "#67E8F9",
            boxShadow: "0 0 4px rgba(103, 232, 249, 0.9)",
          }}
        />
      )}
      <span style={{ fontSize: "clamp(5px, 1vw, 10px)", color: rgba(cat.c, 0.95), lineHeight: 1 }}>{el.number}</span>
      <span style={{ fontSize: "clamp(9px, 2.1vw, 17px)", fontWeight: 800, lineHeight: 1.1, color: dimmed ? "#9CA3AF" : "#fff" }}>{el.symbol}</span>
      {/* Name hidden once cells get small (portrait) — it just becomes noise;
          the symbol + tap-for-detail is the interaction that matters. */}
      <span style={{ fontSize: "clamp(0px, 0.85vw, 8px)", color: "#9CA3AF", lineHeight: 1, overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%", whiteSpace: "nowrap" }}>{el.name}</span>
    </button>
  );
}

function DetailRow({ k, v }) {
  if (v === null || v === undefined || v === "") return null;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "9px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
      <span style={{ fontSize: 13, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600 }}>{k}</span>
      <span style={{ fontSize: 14, color: "#E2E8F0", textAlign: "right" }}>{v}</span>
    </div>
  );
}

function DetailPanel({ el, onClose, pinned, onTogglePin, maxedOut }) {
  const cat = CAT[el.category] || CAT.unknown;
  // V62.5 — "+ Compare" button. Three visual states:
  //   1. Default: "+ Compare" (cyan stroke, click to pin)
  //   2. Already pinned: "✓ Pinned" (cyan-filled, click to unpin)
  //   3. Max-out (4 pinned and this one isn't): "Compare full (4)" (disabled)
  const CYAN = "#67E8F9";
  const CYAN_DARK = "#0E7490";
  const compareDisabled = !pinned && maxedOut;
  let compareBg, compareColor, compareBorder, compareLabel;
  if (pinned) {
    compareBg = rgba(CYAN, 0.18); compareColor = CYAN; compareBorder = CYAN;
    compareLabel = "✓ Pinned";
  } else if (compareDisabled) {
    compareBg = "transparent"; compareColor = "#4B5563"; compareBorder = "#374151";
    compareLabel = "Compare full (4)";
  } else {
    compareBg = "transparent"; compareColor = CYAN; compareBorder = CYAN;
    compareLabel = "+ Compare";
  }
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#11131F", border: `1px solid ${rgba(cat.c, 0.35)}`,
          borderRadius: 18, maxWidth: 460, width: "100%", maxHeight: "85vh",
          overflowY: "auto", padding: "26px 24px",
          boxShadow: `0 0 50px ${rgba(cat.c, 0.15)}`,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 64, height: 64, borderRadius: 14, background: rgba(cat.c, 0.16),
              border: `1px solid ${rgba(cat.c, 0.45)}`, display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
            }}>
              <span style={{ fontSize: 11, color: cat.c }}>{el.number}</span>
              <span style={{ fontSize: 26, fontWeight: 800, color: "#fff", lineHeight: 1 }}>{el.symbol}</span>
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "#fff" }}>{el.name}</h2>
              <span style={{ fontSize: 13, color: cat.c, fontWeight: 600 }}>{cat.label}</span>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{
            background: "rgba(255,255,255,0.05)", border: "none", color: "#9CA3AF",
            width: 34, height: 34, borderRadius: 9, cursor: "pointer", fontSize: 18, fontFamily: "inherit",
          }}>✕</button>
        </div>

        {/* V62.5 — Compare action row. Sits between identity header and the
            field rows. Cyan pairs with the pinned-indicator color on the grid. */}
        <div style={{ marginBottom: 16 }}>
          <button
            onClick={() => onTogglePin && onTogglePin(el.symbol)}
            disabled={compareDisabled}
            aria-pressed={pinned}
            title={
              compareDisabled
                ? "Maximum 4 elements can be pinned for compare"
                : pinned
                  ? "Remove from compare"
                  : "Pin to compare with other elements"
            }
            style={{
              width: "100%",
              padding: "10px 14px",
              background: compareBg,
              color: compareColor,
              border: `1px solid ${compareBorder}`,
              borderRadius: 10,
              fontSize: 14, fontWeight: 600,
              fontFamily: "inherit",
              cursor: compareDisabled ? "not-allowed" : "pointer",
              opacity: compareDisabled ? 0.6 : 1,
              transition: "background 0.15s, color 0.15s, border-color 0.15s",
            }}
          >{compareLabel}</button>
        </div>

        <DetailRow k="Atomic mass" v={`${Array.isArray(el.mass) ? `[${el.mass[0]}]` : el.mass} u`} />
        <DetailRow k="Electron config" v={el.config} />
        <DetailRow k="State (room temp)" v={el.state === "unknown" ? null : el.state} />
        <DetailRow k="Density" v={el.density != null ? `${el.density} g/cm³` : null} />
        <DetailRow k="Melting point" v={el.melt != null ? `${el.melt} K` : null} />
        <DetailRow k="Boiling point" v={el.boil != null ? `${el.boil} K` : null} />
        <DetailRow k="Electronegativity" v={el.electroneg != null ? el.electroneg : null} />
        <DetailRow k="Year discovered" v={el.discovered} />
      </div>
    </div>
  );
}

export default function PeriodicTable() {
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  // V64.1 — quizMode replaces the old quiz boolean. null = no quiz active,
  // "study" = flashcard QuizMode (legacy behavior), "mc" = multi-choice.
  const [quizMode, setQuizMode] = useState(null);
  // V42 Pass 5 — category filter state. null = no filter (default).
  // Set by clicking a legend swatch; cleared by clicking the active swatch again.
  const [selectedCat, setSelectedCat] = useState(null);
  // V62.5 — compare-view pin state. Array of element SYMBOLS (not objects)
  // so equality is cheap and the array doesn't hold stale snapshots if
  // ELEMENTS itself ever changes. Capped at 4 per spec. Does NOT persist
  // across reloads — pinned-state is transient.
  const [pinned, setPinned] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);

  const togglePin = (symbol) => {
    setPinned((cur) => {
      if (cur.includes(symbol)) return cur.filter((s) => s !== symbol);
      if (cur.length >= 4) return cur; // cap — DetailPanel disables the button at 4
      return [...cur, symbol];
    });
  };
  const unpin = (symbol) => setPinned((cur) => cur.filter((s) => s !== symbol));
  const clearPins = () => { setPinned([]); setCompareOpen(false); };

  // Resolve pinned symbols to full element objects for the compare view.
  // useMemo so we don't allocate a new array on every render.
  const pinnedElements = useMemo(
    () => pinned.map((s) => elements.find((e) => e.symbol === s)).filter(Boolean),
    [pinned],
  );

  // Curated recall over the element data already in this tool. Prompt
  // = element name, answer = symbol/number/mass/category.
  const quizCards = elements.map((el) => ({
    q: el.name,
    a: `Symbol: ${el.symbol} · Atomic #: ${el.number} · Mass: ${Array.isArray(el.mass) ? `[${el.mass[0]}]` : el.mass} · ${el.category}`,
    ctx: `Period ${el.row}`,
  }));

  const q = query.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q) return null;
    return new Set(
      elements
        .filter((e) =>
          e.name.toLowerCase().includes(q) ||
          e.symbol.toLowerCase() === q ||
          String(e.number) === q
        )
        .map((e) => e.number)
    );
  }, [q]);

  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", fontFamily: "'DM Sans', system-ui, sans-serif", color: "#E2E8F0" }}>
      <div style={{ position: "fixed", top: "-25%", left: "-10%", width: "50vw", height: "50vw", background: `radial-gradient(circle, ${rgba(EM, 0.05)} 0%, transparent 65%)`, pointerEvents: "none" }} />

      <header style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "16px 24px",
        paddingTop: "calc(16px + env(safe-area-inset-top))",
        paddingLeft: "calc(24px + env(safe-area-inset-left))",
        paddingRight: "calc(24px + env(safe-area-inset-right))",
        borderBottom: `1px solid ${rgba(EM, 0.06)}`, position: "relative", zIndex: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <ChemBackButton accentColor={EM} />
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg, ${EM}, ${EM_DEEP})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800, color: "#fff" }}>Ch</div>
          <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px", background: `linear-gradient(135deg, ${EM_LIGHT}, #A7F3D0)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Periodic Table</span>
          <span style={{ fontSize: 11, color: "#374151", marginLeft: 4 }}>by NerdChem</span>
        </div>
        <div style={{ fontSize: 12, color: "#374151" }}>100% free</div>
      </header>

      <main style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 16px 80px", paddingLeft: "calc(16px + env(safe-area-inset-left))", paddingRight: "calc(16px + env(safe-area-inset-right))", position: "relative", zIndex: 10 }}>
        <p style={{ fontSize: 15, color: "#9CA3AF", lineHeight: 1.6, maxWidth: 680, margin: "0 auto 20px", textAlign: "center" }}>
          The complete interactive periodic table — all 118 elements. Tap any element for its
          atomic mass, electron configuration, state, density, melting and boiling points, and
          discovery year. Free, no signup, works offline once installed.
        </p>

        <div style={{ display: "flex", justifyContent: "center", gap: 10, margin: "0 auto 20px", flexWrap: "wrap" }}>
          <button
            onClick={() => setQuizMode("mc")}
            style={{ padding: "11px 18px", borderRadius: 10, border: `1px solid ${EM}`, background: `${EM}18`, color: EM, fontWeight: 700, fontSize: 14, cursor: "pointer" }}
          >
            Quiz me on the elements →
          </button>
          <button
            onClick={() => setQuizMode("study")}
            style={{ padding: "11px 18px", borderRadius: 10, border: `1px solid ${EM}55`, background: "transparent", color: EM, fontWeight: 600, fontSize: 14, cursor: "pointer" }}
          >
            Study mode (flashcards) →
          </button>
        </div>

        {quizMode === "mc" ? (
          <PeriodicTableMCQuiz elements={elements} accent={EM} onExit={() => setQuizMode(null)} />
        ) : quizMode === "study" ? (
          <QuizMode cards={quizCards} accent={EM} onExit={() => setQuizMode(null)} />
        ) : (
        <>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 22 }}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, symbol, or atomic number…"
            style={{
              width: "100%", maxWidth: 420, padding: "11px 16px", borderRadius: 11,
              border: `1px solid ${rgba(EM, 0.18)}`, background: "rgba(0,0,0,0.3)",
              color: "#E2E8F0", fontSize: 15, fontFamily: "inherit", outline: "none", boxSizing: "border-box",
            }}
          />
        </div>

        {/* The grid always fits the viewport width (18 fractional columns,
            no fixed minimum). Full table is visible at a glance in landscape;
            in portrait the cells are small but the whole table is still
            present — students pinch-zoom to read a cell, which matches how
            everyone already uses a periodic table. No rotate hint: the
            periodic table is universally known to be wide, the instinct to
            turn the phone is already there. */}
        <div style={{ width: "100%" }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(18, 1fr)",
            gridTemplateRows: "repeat(7, auto) clamp(6px, 1.5vw, 14px) repeat(2, auto)",
            gap: "clamp(1px, 0.4vw, 4px)",
            width: "100%",
          }}>
            {elements.map((el) => {
              // V42 Pass 5 — 3-way dim logic per locked spec:
              //   inGroup = no filter OR el matches active filter
              //   inSearch = no search OR el matches active search
              //   dimmed = !inGroup && !inSearch
              // dimmed cells are NOT clickable (blocked); search-matches
              // unblock cells outside the active category (typed search
              // transcends the filter — kid named the element by name,
              // they know what they want — see SESSION-RESTORE 2026-05-20).
              const inGroup = !selectedCat || el.category === selectedCat;
              const inSearch = !matches || matches.has(el.number);
              const dimmed = !inGroup && !inSearch;
              const glowing = !!selectedCat && el.category === selectedCat;
              // Search-outline ONLY when both filter AND search active AND
              // this cell is search-matched but outside the active category.
              const searchOutlined = !!selectedCat && !!matches && !inGroup && inSearch;
              return (
                <ElementCell
                  key={el.number}
                  el={el}
                  onClick={setSelected}
                  dimmed={dimmed}
                  glowing={glowing}
                  searchOutlined={searchOutlined}
                  pinned={pinned.includes(el.symbol)}
                />
              );
            })}
          </div>
        </div>

        {/* Category legend — V42 Pass 5: clickable filter toggles.
            Each swatch toggles selectedCat (click again to clear).
            Active swatch echoes the cell glow as state indicator —
            no text label needed; legend IS the indicator. */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px", justifyContent: "center", marginTop: 26 }}>
          {Object.entries(CAT).filter(([, c]) => c.label !== "Unknown").map(([key, c]) => {
            const active = selectedCat === key;
            return (
              <button
                key={c.label}
                onClick={() => setSelectedCat(active ? null : key)}
                aria-pressed={active}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "4px 8px", borderRadius: 6,
                  background: active ? rgba(c.c, 0.15) : "transparent",
                  border: active ? `1px solid ${c.c}` : "1px solid transparent",
                  boxShadow: active ? `0 0 10px ${rgba(c.c, 0.55)}` : "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  transition: "background 0.15s, border-color 0.15s, box-shadow 0.15s",
                }}
              >
                <span style={{
                  width: 12, height: 12, borderRadius: 3,
                  background: rgba(c.c, active ? 0.7 : 0.4),
                  border: `1px solid ${c.c}`,
                }} />
                <span style={{ fontSize: 12, color: active ? "#E2E8F0" : "#9CA3AF", fontWeight: active ? 600 : 400 }}>{c.label}</span>
              </button>
            );
          })}
        </div>

        <div style={{ marginTop: 40, padding: "20px", borderRadius: 16, background: "linear-gradient(135deg, rgba(16,185,129,0.06), rgba(5,150,105,0.06))", border: "1px solid rgba(16,185,129,0.12)", textAlign: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: EM_LIGHT, marginBottom: 6 }}>More chemistry tools coming to NerdChem</div>
          <div style={{ fontSize: 13, color: "#6B7280" }}>Molecular weight, mole calculator, equation balancer, stoichiometry & more.</div>
        </div>
        </>
        )}
      </main>

      <footer style={{ textAlign: "center", padding: 24, fontSize: 12, color: "#1F2937", borderTop: `1px solid ${rgba(EM, 0.04)}` }}>
        <NerdStackFooter variant="tool" family="chem" />
      </footer>

      {selected && (
        <DetailPanel
          el={selected}
          onClose={() => setSelected(null)}
          pinned={pinned.includes(selected.symbol)}
          onTogglePin={togglePin}
          maxedOut={pinned.length >= 4}
        />
      )}

      {/* V62.5 — floating "Compare (N)" launcher.
          Appears bottom-right (mobile + desktop both — predictable
          thumb-reach) only when 2+ elements are pinned. Cyan to match
          the pinned-cell indicator. Lives at z=120 so it sits above
          the grid but below open modals (DetailPanel z=100 is below
          this; modals like PeriodicCompare at z=200 cover it). */}
      {pinned.length >= 2 && !compareOpen && (
        <button
          onClick={() => setCompareOpen(true)}
          aria-label={`Open compare view with ${pinned.length} pinned elements`}
          style={{
            position: "fixed",
            right: "calc(20px + env(safe-area-inset-right))",
            bottom: "calc(20px + env(safe-area-inset-bottom))",
            zIndex: 120,
            padding: "12px 18px",
            background: "#67E8F9",
            color: "#06283D",
            border: "none",
            borderRadius: 999,
            fontSize: 14, fontWeight: 700,
            fontFamily: "inherit",
            cursor: "pointer",
            boxShadow: "0 8px 24px rgba(103, 232, 249, 0.45)",
            display: "flex", alignItems: "center", gap: 8,
          }}
        >
          <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 22, height: 22, borderRadius: 999,
            background: "rgba(6, 40, 61, 0.18)",
            fontSize: 12, fontWeight: 800,
          }}>{pinned.length}</span>
          <span>Compare</span>
        </button>
      )}

      {/* V62.5 — Compare view modal. Mounted only while compareOpen, but
          unmounting on close preserves the pinned list (it lives in
          PeriodicTable state). Closing via Escape, the X button, or
          backdrop click all just close the modal — they don't clear
          pins. "Clear all" inside the modal explicitly clears + closes. */}
      {compareOpen && pinned.length >= 2 && (
        <PeriodicCompare
          pinned={pinnedElements}
          onUnpin={unpin}
          onClear={clearPins}
          onClose={() => setCompareOpen(false)}
        />
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        input::placeholder { color: #4B5563; }
        * { box-sizing: border-box; }
        body { margin: 0; }
      `}</style>
    </div>
  );
}
